import React, { MouseEventHandler, useEffect, useState } from 'react'
import { UserInfo, UserParticipantContext } from '../contexts'
import { DropdownContext, LanguageContext } from '../contexts'
import { RegistrationMessage } from '../Models/system'
import { Workshop, WorkshopEvent } from '../Models/workshops'
import { RegistrationForm} from '../RegistrationForm/RegistrationForm'
import { RegistrationOperation } from '../Models/participant'
import { DeregistrationModal } from '../RegistrationForm/DeregistrationModal'
import { CardModal } from '../Components/Modal'
import { WorkshopEventsView } from './WorkshopEventsView'
import { FilterAction, FilterSeed, initFilterState, reduceFilterState } from './FilterState'
import { EventViewType } from './EventViewToggle'
import { clickWasOnCalendarEvent } from './EventCalendar/EventCalendar'
import classNames from 'classnames'
import { Config, SpecialClassNames } from '../constants'
import { useInterval } from '../Hooks/Interval'

interface WorkshopEventsProps {
	workshop: Workshop
	workshopEventIdFromQueryString?: number
}

type ModalType = RegistrationOperation | 'LoginPrompt' | 'PrerequisitesPrompt'

export function WorkshopEvents(props: WorkshopEventsProps) {

	const { localizedText, language } = React.useContext(LanguageContext)
	const dropdownData = React.useContext(DropdownContext)
	const { refreshWorkshops } = React.useContext(UserParticipantContext)

	const filterSeed: FilterSeed = {
		dropdownData, 
		workshopEvents: props.workshop.Events
	}

	const [filterState, dispatchFilterAction] = React.useReducer(reduceFilterState, filterSeed, initFilterState)
	const [selectedEventView, setEventView] = React.useState<EventViewType>('List')

	const [selectedWorkshopEventId, setWorkshopEventId] = useState<number | undefined>(props.workshopEventIdFromQueryString)

	const [visibleModal, setVisibleModal] = useState<ModalType>()
	const [recentRegistrationMessages, setRecentRegistrationMessages] = useState<{ [val: number]: RegistrationMessage }>({})

	function getRegistrationMessage(operation: RegistrationOperation): RegistrationMessage {
		if (operation == 'Register') {
			return {
				color: 'success',
				text: localizedText.RegistrationSuccessful
			}

		}
		else if (operation == 'Deregister') {
			return {
				color: 'danger',
				text: localizedText.UnregistrationSuccessful
			}
		}
		else throw 'Unsupported registration operation'
	}

	// every x seconds we update times remaining for registration
	// and filter out any events that have passed cutoff if necessary
	useInterval(() => {
		console.log(Config.RegistrationCutoffCheckInterval)
		dispatchFilterAction({ type: 'FilterEvents' })
	}, Config.RegistrationCutoffCheckInterval * 1_000)

	// set workshop Event Id if query string changes
	useEffect(() => {
		setWorkshopEventId(props.workshopEventIdFromQueryString)
	}, [props.workshopEventIdFromQueryString])

	// swap out filters if we change language
	useEffect(() => {
		dispatchFilterAction({ type: 'GuiLanguageChange', dropdownData })
	}, [dropdownData])

	// reset filters and ranges if we move to another workshop
	useEffect(() => {
		const seed: FilterSeed = {
			dropdownData,
			workshopEvents: props.workshop.Events
		}
		const action: FilterAction = { type: 'WorkshopChange', seed }
		dispatchFilterAction(action)

	}, [props.workshop.WorkshopId])

	// update workshop events if we needed to reset them (such as after registration changes)
	useEffect(() => {
		if (filterState.domains.workshopEvents != props.workshop.Events) {
			dispatchFilterAction({type: 'WorkshopUpdate', updatedWorkshopEvents: props.workshop.Events})
		}
	}, [props.workshop.Events])

	// setup listener to close expanded event if we click outside of it 
	useEffect(() => {
		function globalClickListener(event: MouseEvent) {
			if (!clickWasOnCalendarEvent(event) && selectedWorkshopEventId && !visibleModal) { //&&
				// we have a selected event and the registration form is not open 
				// so lets collapse the currently expanded one
				setWorkshopEventId(undefined)
			}
		}

		// register listener
		document.addEventListener('click', globalClickListener)

		// cleanup function deregisters it when element is unmounted or dependant variables change
		return () => {
			document.removeEventListener('click', globalClickListener)
		}
	}, [visibleModal, selectedWorkshopEventId, selectedEventView])


	// Registration form modal logic
	/**********************************/
	function handleRegisterClick(operation: RegistrationOperation) {
		if (!UserInfo.isUserLoggedIn()) setVisibleModal('LoginPrompt')
		else if (operation == 'Register' && props.workshop.Prerequisites[language]) setVisibleModal('PrerequisitesPrompt')
		else setVisibleModal(operation)
	}

	function handleRegisterationOperationConfirmation(eventId: number, operation: RegistrationOperation) {

		// show operation successful message in appropriate expanded event
		const newMessages = Object.assign({}, recentRegistrationMessages)
		newMessages[eventId] = getRegistrationMessage(operation)
		setRecentRegistrationMessages(newMessages)

		// close popups
		setVisibleModal(undefined)
	}

	function handleRegisterCancel(workshopRefreshRequired?: boolean) {
		setVisibleModal(undefined)
		if (workshopRefreshRequired) refreshWorkshops?.()
	}

	function handleRegistrationMessageDismissal(workshopEventId: number) {
		const newMessages = Object.assign({}, recentRegistrationMessages)
		delete newMessages[workshopEventId]
		setRecentRegistrationMessages(newMessages)
	}
	/**********************************/

	const getWorkshopEventClickHandler = (event: WorkshopEvent) => {
		const handler: MouseEventHandler = (e) => {
			if (event.EventId) setWorkshopEventId(event.EventId)

			// mark which event we're examining in the query string
			// this allows redirecting back to this event if we're not logged in
			const { history, location } = window
			history.replaceState(history.state, '', `${location.pathname}?workshopEventId=${event.EventId}`)

			// we stop propagation so that our global click listener doesn't interfere with state 
			// and try to close an expanded event
			e.stopPropagation && e.stopPropagation()
		}
		return handler
	}

	let modal: React.ReactElement | null = null

	if (selectedWorkshopEventId) {
		const selectedWorkshopEvent = filterState.ranges.filteredWorkshopEvents.find(e => e.EventId == selectedWorkshopEventId)

		// it's possible to have a workshop event get cutoff and expire while a user is registering
		if (!selectedWorkshopEvent) {
			setWorkshopEventId(undefined)
			setVisibleModal(undefined)
		} 
		else if (visibleModal == 'Register') {
			modal = <RegistrationForm workshopEvent={selectedWorkshopEvent} onCancel={handleRegisterCancel} onConfirmRegister={handleRegisterationOperationConfirmation} />
		}
		else if (visibleModal == 'Deregister') {
			modal = <DeregistrationModal workshopEventId={selectedWorkshopEventId} onCancel={handleRegisterCancel} onConfirmDeregister={handleRegisterationOperationConfirmation} /> 
		}
		else if (visibleModal == 'LoginPrompt') {
			modal = 
				<CardModal 
					title={localizedText.LoginRequired} 
					onCancel={() => setVisibleModal(undefined)} 
					primaryButton={{ text: localizedText.ProceedToOntarioProgramsNet, color: 'primary', onClick: UserInfo.loginAndReturn }} 
					className={classNames(SpecialClassNames.narrowModal)}
					>
					{localizedText.LoginRequiredMessage}
				</CardModal>
		}
		else if (visibleModal == 'PrerequisitesPrompt') {
			modal = 
				<CardModal 
					title={localizedText.PrerequisiteRequired} 
					onCancel={() => setVisibleModal(undefined)} 
					primaryButton={{ text: localizedText.Confirm, color: 'primary', onClick: () => setVisibleModal('Register') }} 
					className={classNames(SpecialClassNames.narrowModal)}
					>
					{props.workshop.Prerequisites[language]}
				</CardModal>
		}
	}

	return (
		<WorkshopEventsView 
			workshop={props.workshop}
			onRegisterClick={handleRegisterClick}
			getWorkshopEventClickHandler={getWorkshopEventClickHandler}
			selectedWorkshopEventId={selectedWorkshopEventId}
			filterState={filterState}
			dispatchFilterAction={dispatchFilterAction}
			recentRegistrationMessages={recentRegistrationMessages}
			onRegistrationMessageDismissal={handleRegistrationMessageDismissal}
			selectedEventView={selectedEventView}
			onSelectEventView={setEventView}
			modal={modal}
			selectWorkshopEventId={setWorkshopEventId}
		/>
	)
}

export default WorkshopEvents